import { cn } from "@eisox/design-system";
import { HeatIcon, SnowFlackIcon } from "@eisox/icons";

import { getSetpointLines } from "../helpers";
import { Position, type Component } from "../types";
import { Card } from "./card";
import { DefectDot } from "./defect-dot";
import { Indicator } from "./indicator";
import { SetpointLine } from "./setpoint-line";

interface TemperatureSensorProps extends Component {
  buildingId: string;
  airHandlerUnitId: string;
  temperature?: number;
  defect?: boolean;
  setpoint?: number;
  actualSetpoint?: number;
  coolingSetpoint?: number;
  coolingActualSetpoint?: number;
  className?: string;
}

const TemperatureSensor: React.FC<TemperatureSensorProps> = ({
  buildingId,
  airHandlerUnitId,
  temperature,
  defect,
  setpoint,
  actualSetpoint,
  coolingSetpoint,
  coolingActualSetpoint,
  position = Position.TOP,
  className,
}) => {
  const heatingLines = getSetpointLines(actualSetpoint, setpoint);
  const coolingLines = getSetpointLines(coolingActualSetpoint, coolingSetpoint);
  // The couple needs its two quantity icons to be told apart; a lone temperature keeps the target.
  const couple = heatingLines.length > 0 && coolingLines.length > 0;

  return (
    <div className={cn("absolute", className)}>
      <div
        className={cn("gap-1.25 flex w-max flex-col items-center", position === Position.BOTTOM && "flex-col-reverse")}
      >
        {heatingLines.length + coolingLines.length > 0 && (
          <Card className="gap-0 flex flex-col items-start">
            {heatingLines.map(line => (
              <SetpointLine
                key={`heating-${line.kind}`}
                kind={line.kind}
                section="temperature"
                muted={line.muted}
                icon={couple ? HeatIcon : undefined}
                buildingId={buildingId}
                airHandlerUnitId={airHandlerUnitId}
              >
                {`${line.value.toFixed(1)}°C`}
              </SetpointLine>
            ))}
            {coolingLines.map(line => (
              <SetpointLine
                key={`cooling-${line.kind}`}
                kind={line.kind}
                section="temperature"
                muted={line.muted}
                icon={couple ? SnowFlackIcon : undefined}
                buildingId={buildingId}
                airHandlerUnitId={airHandlerUnitId}
              >
                {`${line.value.toFixed(1)}°C`}
              </SetpointLine>
            ))}
          </Card>
        )}
        <Indicator position={position}>
          <span className="gap-1 flex items-center">
            {`${temperature !== undefined ? temperature.toFixed(1) : "--"}°C`}
            <DefectDot defect={defect} />
          </span>
        </Indicator>
      </div>
    </div>
  );
};

TemperatureSensor.displayName = "TemperatureSensor";

export { TemperatureSensor };
export type { TemperatureSensorProps };
